import React, { useState } from 'react'
import {
  Stack,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { SecuritiesReportChunk, SecuritiesReportChunkDetailPageProps } from '../model'
import { requestStatus } from '../../../../_shared/lib'
import * as DomainModel from 'poc-ai-biz-irdb_domain-model'

// ツリー構造を構築するためのヘルパー関数
const buildTreeStructure = (chunks: SecuritiesReportChunk[]) => {
  // チャンクをドキュメント内の出現順（chunkIndex）でソート
  // これにより、階層レベルが上下する自然な文書構造を再現
  const sortedChunks = [...chunks].sort((a, b) => {
    const aIndex = a.chunkIndex ?? 0
    const bIndex = b.chunkIndex ?? 0
    return aIndex - bIndex
  })

  return sortedChunks
}

export const SecuritiesReportChunkDetailPagePresentation: React.FC<SecuritiesReportChunkDetailPageProps> = ({
  currentSecuritiesReportChunk,
  allSecuritiesReportChunks,
  onSecuritiesReportChunkClick,
  onBreadcrumbClick,
}) => {
  const currentSecuritiesReportChunkStatus = requestStatus(currentSecuritiesReportChunk)
  const allSecuritiesReportChunksStatus = requestStatus(allSecuritiesReportChunks)

  // エラー状態
  if (currentSecuritiesReportChunkStatus === 'error') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100vh', p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          有価証券報告書の取得ができませんでした。
          <br />
          {currentSecuritiesReportChunk.error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          ページを再読み込み
        </Button>
      </Stack>
    )
  }

  const currentSecuritiesReportChunkData = currentSecuritiesReportChunk.value
  return (
    <Stack sx={{ height: '100vh', overflow: 'hidden' }}>
      {/* ヘッダー */}
      <Stack spacing={2} sx={{ p: 3, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        {!currentSecuritiesReportChunkData ? (
          <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <Stack>
            {/* ドキュメント情報 */}
            <Typography variant="h5" sx={{ mt: 1 }}>
              {currentSecuritiesReportChunkData.filerName} {currentSecuritiesReportChunkData.docDescription || '-'}
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* コンテンツエリア */}
      <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
        <Box py={3} sx={{ width: 420, borderLeft: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
          <Box px={2} sx={{ overflow: 'auto', height: '100%' }}>
            {allSecuritiesReportChunksStatus === 'requesting' && (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  目次を読み込んでいます...
                </Typography>
              </Stack>
            )}

            {allSecuritiesReportChunksStatus === 'error' && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{allSecuritiesReportChunks.error}</Alert>
              </Box>
            )}

            {allSecuritiesReportChunksStatus === 'success' && allSecuritiesReportChunks.value && (
              <>
                {allSecuritiesReportChunks.value.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      項目がありません
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={0}>
                    {(() => {
                      if (!allSecuritiesReportChunks.value) return null
                      const sortedChunks = buildTreeStructure(allSecuritiesReportChunks.value)

                      return sortedChunks.map((chunk) => {
                        const isCurrentChunk = currentSecuritiesReportChunkData?.id === chunk.id
                        const indentLevel = chunk.titlesStack.length - 1 // 0-based indent
                        const isClickable = chunk.isLeaf

                        return (
                          <Box
                            key={chunk.id}
                            onClick={isClickable ? () => onSecuritiesReportChunkClick(chunk) : undefined}
                            sx={{
                              cursor: isClickable ? 'pointer' : 'default',
                              py: 1,
                              px: 2,
                              ml: indentLevel * 3, // インデントレベルに応じて左マージンを設定
                              fontWeight: isClickable ? 600 : 'normal',
                              backgroundColor: isCurrentChunk ? 'primary.main' : 'transparent',
                              color: isCurrentChunk ? 'primary.contrastText' : 'text.primary',
                              '&:hover': isClickable
                                ? {
                                    backgroundColor: isCurrentChunk ? 'primary.dark' : 'action.hover',
                                  }
                                : {},
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {chunk.title}
                            </Typography>
                          </Box>
                        )
                      })
                    })()}
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* メインコンテンツ */}
        <Stack px={6} py={3} sx={{ flex: 1, overflow: 'auto' }}>
          {!currentSecuritiesReportChunkData ? (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                有価証券報告書を読み込んでいます...
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Breadcrumbs separator=">">
                  {currentSecuritiesReportChunkData.titlesStack.map((title, index) => {
                    return (
                      <Typography key={index} variant="body1" sx={{ cursor: 'default' }}>
                        {title}
                      </Typography>
                    )
                  })}
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {currentSecuritiesReportChunkData.title}
                </Typography>
              </Stack>
              {/* SecuritiesReportChunkの内容を表示 */}
              <Box
                sx={{
                  color: 'text.primary',
                  '& table': {
                    border: '1px solid #cccccc',
                    borderCollapse: 'collapse',
                    width: '100%',
                  },
                  '& td, & th': {
                    border: '1px solid #cccccc',
                    padding: '8px',
                  },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    marginTop: '1.5rem',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                  },
                  '& p': {
                    marginBottom: '1rem',
                    lineHeight: 1.6,
                  },
                  '& ul, & ol': {
                    marginBottom: '1rem',
                    paddingLeft: '2rem',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: currentSecuritiesReportChunkData.trimmedChunkContent.trim() }}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
